/**
 * ActiveFilters Component
 *
 * Displays active filters as closeable tag chips
 * - Shows filter name, operator, and value
 * - Click X to remove individual filter
 * - "Clear all" button to remove all filters
 */

import React from 'react';
import { Tag, Space, Button } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { UDCFilter, AvailableFilter } from '@/api/types/udc.types';
import './ActiveFilters.css';

interface ActiveFiltersProps {
  filters: UDCFilter[];
  availableFilters: AvailableFilter[];
  onRemoveFilter: (filterId: string) => void;
  onClearAll: () => void;
}

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  filters,
  availableFilters,
  onRemoveFilter,
  onClearAll,
}) => {
  const { formatDate } = useUserTimezone();

  if (filters.length === 0) {
    return null;
  }

  // Get field display name
  const getFieldDisplayName = (columnName: string): string => {
    const field = availableFilters.find((f) => f.name === columnName);
    return field?.display_name || columnName;
  };

  // Get operator label
  const getOperatorLabel = (operator: string): string => {
    const labels: Record<string, string> = {
      equals: '=',
      not_equals: '≠',
      greater_than: '>',
      less_than: '<',
      greater_than_or_equal: '≥',
      less_than_or_equal: '≤',
      contains: 'contains',
      starts_with: 'starts with',
      ends_with: 'ends with',
      in: 'in',
      not_in: 'not in',
      between: 'between',
      on_date: 'on',
      is_null: 'is empty',
      is_not_null: 'is not empty',
    };
    return labels[operator] || operator;
  };

  // Format value for display
  const formatValue = (value: any, operator: string): string => {
    if (value === null || value === undefined) {
      return '';
    }

    // No value display for these operators
    if (operator === 'is_null' || operator === 'is_not_null') {
      return '';
    }

    // Array values (in, not_in, between)
    if (Array.isArray(value)) {
      if (operator === 'between') {
        return `${value[0]} - ${value[1]}`;
      }
      return value.join(', ');
    }

    // Date values - convert UTC to user timezone
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return formatDate(value, 'L');
    }

    // Boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  };

  // Build filter label
  const buildFilterLabel = (filter: UDCFilter): string => {
    const field = getFieldDisplayName(filter.column);
    const operator = getOperatorLabel(filter.operator);
    const value = formatValue(filter.value, filter.operator);

    if (!value) {
      return `${field} ${operator}`;
    }

    return `${field} ${operator} ${value}`;
  };

  return (
    <div className="active-filters">
      <Space size={[8, 8]} wrap>
        <span className="active-filters-label">Active filters:</span>
        {filters.map((filter) => (
          <Tag
            key={filter.id}
            closable
            onClose={() => onRemoveFilter(filter.id)}
            className="active-filter-tag"
            color="blue"
          >
            {buildFilterLabel(filter)}
          </Tag>
        ))}
        {filters.length > 1 && (
          <Button
            type="link"
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={onClearAll}
            className="clear-all-button"
          >
            Clear all
          </Button>
        )}
      </Space>
    </div>
  );
};
